import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { PaginationHooks, getMuiTablePaginationProps } from "../../utils/pagination";

export interface IFoodGroup {
  id: string;
  name: string;
  source: string | null;
  foodsCount: number;
}

interface FoodGroupTableProps {
  rows: IFoodGroup[];
  setSelectedFoodGroup: (foodGroup: IFoodGroup | null) => void;
  setFoodGroupModalOpen: (open: boolean) => void;
  paginationHooks: PaginationHooks;
  total: number;
  setSortOrder: (sortOrder: "ASC" | "DESC") => void;
  sortOrder: "ASC" | "DESC";
  readOnly?: boolean;
  selectable?: boolean;
  selectedIds?: string[];
  onToggleRowSelection?: (id: string) => void;
  onToggleAllSelection?: (pageRowIds: string[], checked: boolean) => void;
}

export const FoodGroupTable = ({
  setFoodGroupModalOpen,
  setSelectedFoodGroup,
  rows: foodGroups,
  paginationHooks,
  total,
  setSortOrder,
  sortOrder,
  readOnly = false,
  selectable = false,
  selectedIds = [],
  onToggleRowSelection,
  onToggleAllSelection,
}: FoodGroupTableProps) => {
  const toggleSortOrder = () => setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
  const selectedSet = new Set(selectedIds);
  const pageRowIds = foodGroups.map(foodGroup => foodGroup.id);
  const allVisibleSelected = pageRowIds.length > 0 && pageRowIds.every(id => selectedSet.has(id));
  const someVisibleSelected = pageRowIds.some(id => selectedSet.has(id));

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <TableContainer sx={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    onChange={(_, checked) => onToggleAllSelection?.(pageRowIds, checked)}
                    inputProps={{ "aria-label": "Select all food groups" }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ width: 300 }}>
                <TableSortLabel onClick={toggleSortOrder}>
                  <b>Name</b>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <b># Foods</b>
              </TableCell>
              {readOnly && (
                <TableCell>
                  <b>Source</b>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {foodGroups.map(foodGroup => {
              const { id, name, foodsCount, source } = foodGroup;
              const isSelected = selectedSet.has(id);
              return (
                <TableRow
                  id={id}
                  key={id}
                  selected={isSelected}
                  onClick={() => {
                    setSelectedFoodGroup(foodGroup);
                    setFoodGroupModalOpen(true);
                  }}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setSelectedFoodGroup(foodGroup);
                      setFoodGroupModalOpen(true);
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  {selectable && (
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onClick={e => {
                          e.stopPropagation();
                          onToggleRowSelection?.(id);
                        }}
                        inputProps={{ "aria-label": `Select ${name}` }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography>{name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{foodsCount}</Typography>
                  </TableCell>
                  {readOnly && (
                    <TableCell>
                      <Typography>{source}</Typography>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        sx={{ ".MuiTablePagination-actions": { mr: 8 } }}
        {...getMuiTablePaginationProps(paginationHooks, total)}
      />
    </Box>
  );
};
